import api from '../httpClient';

import type {
  AdminUserCohortMembershipWriteResponse,
  AdminUserCohortMembership,
} from 'pages/Admin/UserManagement/types';
import type { CohortRole, VoiceProcessingModels, VoiceProcessingModelsSetting } from 'types';

export interface CourseCurriculumPayload {
  metadata?: Record<string, unknown>;
  chapters: Array<{
    id?: number;
    client_id?: string;
    title: string;
    description?: string;
    order: number;
  }>;
  flows: Array<{
    flow_id: number;
    order?: number;
    is_required?: boolean;
    chapter_id?: number | null;
    chapter_key?: string | null;
  }>;
  prerequisite_ids: number[];
}

export type QuestionLibraryView = 'all' | 'unused' | 'shared' | 'empty_text' | 'duplicate_text' | 'recent';

export type QuestionLibrarySearchParams = {
  search?: string;
  question_type?: string;
  course_id?: number | string;
  flow_id?: number | string;
  key_prefix?: string;
  view?: QuestionLibraryView;
  group?: 'duplicates';
  duplicate_of?: number;
  include_inactive?: boolean;
  page?: number;
  page_size?: number;
};

export type QuestionLibraryCatalogCourse = {
  id: number;
  name: string;
  lifecycle_status: string;
  factory_managed: boolean;
  module_count: number;
  question_count: number;
  updated_at: string;
  is_life_plan: boolean;
  looks_like_test: boolean;
  /** Chapters in the course (Course Builder list meta). */
  chapter_count: number;
  /** Active assignments; courses assigned to anyone are listed first in the builders. */
  learner_count: number;
  organization_count: number;
  cohort_count: number;
};

/** Saved views on Module Builder's All modules tab (mirrors the Question Library's chips). */
export type ModuleLibraryView = 'all' | 'unassigned' | 'shared' | 'empty' | 'description_is_name' | 'recent';
export type ModuleLibraryType = 'standard' | 'quiz' | 'roleplay' | 'voice';

export type ModuleLibrarySearchParams = {
  search?: string;
  /** A course id, or 'unassigned' for modules that are not in any course. */
  course_id?: number | 'unassigned';
  type?: ModuleLibraryType;
  view?: ModuleLibraryView;
  /** Module Builder sends true so inactive modules stay findable (marked Inactive). */
  include_inactive?: boolean;
  page?: number;
  page_size?: number;
};

export type ModuleLibraryRow = {
  id: number;
  name: string;
  title: string;
  description: string;
  is_quiz: boolean;
  is_roleplay: boolean;
  voice_mode_enabled: boolean;
  question_count: number;
  /** Active courses that include this module, in name order. */
  courses: Array<{ id: number; name: string }>;
  updated_at: string;
  is_active: boolean;
};

export type ModuleLibraryViewCounts = Record<ModuleLibraryView, number>;

export type QuestionLibraryCourseCatalog = {
  courses: QuestionLibraryCatalogCourse[];
  unassigned_module_count: number;
  /** Inactive modules in no course; Module Builder adds these to its "Modules not in any course" count. */
  unassigned_inactive_module_count?: number;
  unassigned_question_count: number;
  default_course_id: number | null;
};

export type QuestionLibraryTreeQuestion = {
  id: number;
  key: string;
  label: string;
  type: string;
  order: number;
  is_required: boolean;
  is_active: boolean;
  used_in_count: number;
};

export type QuestionLibraryTreeModule = {
  id: number;
  name: string;
  title: string | null;
  chapter_id: number | null;
  order: number;
  is_required: boolean;
  /** False for inactive modules (only listed in the unassigned bucket when include_inactive is sent). */
  is_active?: boolean;
  question_count: number;
  questions: QuestionLibraryTreeQuestion[];
};

export type QuestionLibraryCourseTree = {
  course: { id: number; name: string; is_life_plan: boolean } | null;
  chapters: Array<{ id: number; title: string; order: number }>;
  modules: QuestionLibraryTreeModule[];
};

export type QuestionLibrarySearchRow = {
  id: number;
  key: string;
  label: string;
  header: string | null;
  type: string;
  updated_at: string;
  is_active: boolean;
  used_in_count: number;
  module_count: number;
  modules: Array<{ id: number; name: string }>;
  course_count: number;
  courses: Array<{ id: number; name: string }>;
};

export type QuestionLibraryDuplicateGroup = {
  text_key: string;
  label: string;
  count: number;
  types: string[];
  module_count: number;
  course_count: number;
  sample_question_id: number;
};

export type QuestionLibraryViewCounts = Record<QuestionLibraryView, number>;

export type AdminCatalogPage<T> = {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
};

/** Catalog rows deliberately omit editor content and Course Factory snapshots. */
export type AdminCourseSummary = {
  id: number;
  name: string;
  description: string;
  is_active: boolean;
  lifecycle_status: 'published' | 'qa';
  content_key: string;
  content_version: string;
  factory_managed: boolean;
  flow_count: number;
  chapter_count: number;
  prerequisite_count: number;
};

export type AdminCourseOption = Pick<AdminCourseSummary, 'id' | 'name' | 'is_active' | 'lifecycle_status'> & {
  prerequisite_ids: number[];
};

export type AdminFlowSummary = {
  id: number;
  name: string;
  title: string;
  description: string;
  is_active: boolean;
  is_public: boolean;
  is_quiz: boolean;
  frequency: string | null;
  voice_mode_enabled: boolean;
  passing_score_percentage: number | null;
  max_attempts: number | null;
  allow_retake_after_pass: boolean;
  randomize_question_order: boolean;
  randomize_option_order: boolean;
  estimated_duration_minutes: number | null;
  question_count: number;
};

export type RoleplayPerformanceWindow = '24h' | '7d' | '30d';

export type RoleplayPerformanceBreakdownMetric =
  | 'local_speech_stop_to_audio_ms' | 'speech_stop_to_audio_ms' | 'local_speech_stop_to_provider_ms'
  | 'elevenlabs_connection_ms' | 'elevenlabs_text_buffer_ms' | 'elevenlabs_first_audio_ms'
  | 'elevenlabs_audio_to_playback_ms' | 'elevenlabs_max_audio_gap_ms';

export type RoleplayPerformanceStat = {
  count: number;
  p50_ms: number | null;
  p95_ms: number | null;
};

export type RoleplayPerformanceTrendMetric =
  | 'first_response_ms' | 'controls_ready_ms' | 'server_evaluation_ms'
  | 'local_speech_stop_to_audio_ms' | 'local_speech_stop_to_provider_ms' | 'elevenlabs_first_audio_ms';

export type RoleplayPerformanceTrendBucket = {
  started_at: string;
  ended_at: string;
  partial: boolean;
  count: number;
  error_count: number;
  metrics: Record<RoleplayPerformanceTrendMetric, RoleplayPerformanceStat>;
};

export type RoleplayPerformanceGroup = {
  mode: string;
  input_mode: string;
  provider: string;
  model: string | null;
  count: number;
  error_count: number;
  breakdown?: Partial<Record<RoleplayPerformanceBreakdownMetric, RoleplayPerformanceStat>>;
  trend?: RoleplayPerformanceTrendBucket[];
  metrics: Record<
    'first_response_ms' | 'controls_ready_ms' | 'server_evaluation_ms' | 'server_generation_ms',
    RoleplayPerformanceStat
  >;
};

export type RoleplayPerformanceEvent = {
  id: number;
  recorded_at: string;
  session_id: number;
  turn_id: number | null;
  generation: number | null;
  mode: string;
  input_mode: string;
  provider: string;
  voice: string | null;
  model: string | null;
  app_version: string | null;
  outcome: string;
  error_code: string | null;
  first_response_ms: number | null;
  controls_ready_ms: number | null;
  backend_decision_ms: number | null;
  server_evaluation_ms: number | null;
  server_generation_ms: number | null;
  server_pre_evaluation_ms: number | null;
  server_post_evaluation_ms: number | null;
  breakdown?: Partial<Record<RoleplayPerformanceBreakdownMetric, number | null>>;
  network: {
    rtt_ms: number | null;
    jitter_ms: number | null;
    packet_loss_percent: number | null;
    sample_scope: 'openai_webrtc' | null;
  };
};

export type RoleplayPerformanceReport = {
  window: RoleplayPerformanceWindow;
  trend_interval?: 'hour' | 'day';
  started_at: string;
  generated_at: string;
  retention_days: number;
  total_turns: number;
  failed_turns: number;
  sample_limit: number;
  truncated: boolean;
  slow_threshold_ms: number;
  groups: RoleplayPerformanceGroup[];
  recent_events: RoleplayPerformanceEvent[];
};

export type AdminFlowCompanionSummary = {
  flow_id: number;
  flow_name: string;
  active_session_count: number;
  active_message_count: number;
  latest_session_updated_at: string | null;
  has_crisis_lock: boolean;
};

export type ClearFlowCompanionPayload = {
  user_id: number;
  flow_id: number;
};

export type ClearFlowCompanionResponse = {
  user_id: number;
  flow_id: number;
  flow_name: string;
  archived_session_count: number;
  archived_message_count: number;
};

export type VoiceRealtimeModelSetting = {
  realtime_model: string;
  effective_realtime_model: string;
  environment_realtime_model: string;
  global_realtime_model_override: string | null;
  debug_realtime_model_override: string | null;
  realtime_model_options: Array<{
    id: string;
    label: string;
    description: string;
  }>;
};

export type VoiceRuntime = 'realtime' | 'live';

export type VoiceRuntimeSetting = {
  default_runtime: VoiceRuntime;
  runtime_options: Array<{
    id: VoiceRuntime;
    label: string;
    description: string;
  }>;
};

export type VoiceTranscriptionModelSetting = {
  scope?: 'flow_voice';
  default_transcription_model?: string;
  transcription_model: string;
  effective_transcription_model: string;
  environment_transcription_model: string;
  global_transcription_model_override: string | null;
  transcription_model_options: Array<{
    id: string;
    label: string;
    description: string;
  }>;
};

export type MeetingScheduledEmailsSetting = {
  enabled: boolean;
};

export type RoleplayElevenLabsModelSetting = {
  model_id: string;
  saved_model_id: string | null;
  default_model_id: string;
  model_options: Array<{
    id: string;
    label: string;
    description: string;
  }>;
};

export type RoleplayVoiceOption = {
  alias: string;
  name: string;
  provider: 'openai' | 'elevenlabs';
};

export type RoleplayScorecardCategory = {
  key: string;
  label: string;
  description: string;
  icon: string;
};

/** Unsaved roleplay question draft used to suggest scorecard categories. */
export type RoleplayScorecardSuggestionRequest = {
  title: string;
  situation: string;
  learner_role: string;
  learning_objectives: string[];
  win_condition_prompt: string;
  actor_prompt: string;
};

export type RoleplayScorecardSuggestionResponse = {
  categories: RoleplayScorecardCategory[];
};

export type ElevenLabsRoleplayVoice = {
  voice_id: string;
  name: string;
  category: string;
  description: string;
  labels: Record<string, string>;
  preview_url: string | null;
  high_quality_base_model_ids: string[];
  is_model_compatible: boolean;
  is_owner: boolean;
  is_legacy: boolean;
  available: boolean;
  mapped_aliases?: string[];
};

export type ElevenLabsRoleplayVoiceUsage = {
  question_count: number;
  questions: Array<{
    id: number;
    key: string;
    title: string;
    is_active: boolean;
  }>;
  active_session_count: number;
};

export type ElevenLabsRoleplayVoiceMapping = {
  alias: string;
  name: string;
  voice: ElevenLabsRoleplayVoice;
  usage: ElevenLabsRoleplayVoiceUsage;
  can_remove: boolean;
};

export type ElevenLabsRoleplayVoiceLibrary = {
  provider_configured: boolean;
  provider_connected: boolean;
  provider_error: string | null;
  model_id: string;
  mappings: ElevenLabsRoleplayVoiceMapping[];
  voices: ElevenLabsRoleplayVoice[];
};

export type ElevenLabsSharedVoice = {
  public_owner_id: string;
  voice_id: string;
  name: string;
  accent: string;
  gender: string;
  age: string;
  descriptive: string;
  use_case: string;
  category: string;
  language: string;
  description: string;
  preview_url: string | null;
  rate: number | null;
  free_users_allowed: boolean;
};

export type ElevenLabsSharedVoiceSearchResult = {
  voices: ElevenLabsSharedVoice[];
  page: number;
  page_size: number;
  has_more: boolean;
  total_count: number | null;
};

export type QueuedAdminJobResponse = {
  task_id: string;
  status: 'queued';
  job_name: string;
};

export type ContentSyncState =
  | 'in_sync'
  | 'site_changes_pending'
  | 'agent_draft_pending'
  | 'reconciliation_required';

export type ContentSyncSummary = {
  state: ContentSyncState;
  project_id: string;
  content_key: string;
  source_revision: number;
  released_source_revision: number | null;
  site_revision_id: string | null;
  site_revision: number | null;
  site_changes_pending: boolean;
  agent_draft_pending: boolean;
};

export type FactoryCourseImpact = ContentSyncSummary & {
  course_id: number;
  course_name: string;
};

export type AnalyticsOperationsJobStatus = {
  job_name: string;
  status: string;
  started_at: string | null;
  finished_at: string | null;
  duration_ms: number | null;
  summary: Record<string, unknown>;
  errors: string[];
};

export type AnalyticsOperationsStatus = {
  backfill_complete: boolean;
  last_successful_run_at: string | null;
  rollup_version: number;
  cache_namespace_version: number;
  last_processed_event_at: string | null;
  last_processed_event_id: number | null;
  last_processed_bug_report_at: string | null;
  last_processed_bug_report_id: number | null;
  last_processed_user_joined_at: string | null;
  last_processed_user_id: number | null;
  recent_jobs: AnalyticsOperationsJobStatus[];
};

export type AdminInspireOwner = { id: string; name: string; org_id: string };
export type AdminInspireCard = {
  id: string;
  key: string;
  owner_organization?: AdminInspireOwner | null;
  prompt: string;
  helper_text: string;
  facilitator_notes: string;
  version: number;
  status: 'draft' | 'published' | 'archived';
  media_asset?: string | null;
  media_url?: string | null;
};
export type AdminInspireDeck = {
  id: string;
  key: string;
  name: string;
  description: string;
  helper_text: string;
  facilitator_notes: string;
  accent_color: string;
  owner_organization?: AdminInspireOwner | null;
  status: 'draft' | 'published' | 'archived';
  items: Array<{ id: string; order: number; card: AdminInspireCard }>;
};

export const adminContent = {
  inspireDecks: {
    list: (organizationId: string) => api.get('/api/admin/inspire-decks/', { params: { organization_id: organizationId } }),
    create: (payload: Record<string, unknown>) => api.post('/api/admin/inspire-decks/', payload),
    update: (id: string, payload: Record<string, unknown>) => api.patch(`/api/admin/inspire-decks/${id}/`, payload),
    archive: (id: string) => api.delete(`/api/admin/inspire-decks/${id}/`),
    setCards: (id: string, cardIds: string[]) => api.put(`/api/admin/inspire-decks/${id}/cards/`, { card_ids: cardIds }),
  },
  inspireCards: {
    list: (organizationId: string) => api.get('/api/admin/inspire-cards/', { params: { organization_id: organizationId } }),
    create: (payload: Record<string, unknown>) => api.post('/api/admin/inspire-cards/', payload),
    update: (id: string, payload: Record<string, unknown>) => api.patch(`/api/admin/inspire-cards/${id}/`, payload),
    archive: (id: string) => api.delete(`/api/admin/inspire-cards/${id}/`),
    customize: (id: string, organizationId: string) => api.post(`/api/admin/inspire-cards/${id}/customize/`, { organization_id: organizationId }),
  },
  roleplayPersonas: {
    list: () => api.get('/api/admin/roleplay-personas/'),
  },
  questions: {
    list: (params?: {
      search?: string;
      question_type?: string;
      page?: number;
      page_size?: number;
      is_active?: boolean | string;
      course_id?: string | number;
      flow_id?: string | number;
      include_inactive?: boolean | string;
    }) => api.get('/api/admin/question-library/', { params }),
    retrieve: (id: number, params?: { include_inactive?: boolean }) =>
      api.get(`/api/admin/question-library/${id}/`, params ? { params } : undefined),
    retrieveByKey: (key: string) =>
      api.get(`/api/admin/question-library/by-key/${encodeURIComponent(key)}/`),
    upsert: (payload: Record<string, unknown>, params?: { include_inactive?: boolean }) => {
      const { id, ...rest } = payload;
      if (typeof id === 'number' && id > 0) {
        return api.patch(`/api/admin/question-library/${id}/`, rest, params ? { params } : undefined);
      }
      return api.post('/api/admin/question-library/', rest);
    },
    preview: (payload: Record<string, unknown>) =>
      api.post('/api/admin/question-library/preview/', payload),
    listTypes: () => api.get('/api/admin/question-library/question-types/'),
    listCourses: () => api.get('/api/admin/question-library/courses/'),
    listFlows: (params?: { course_id?: string | number }) =>
      api.get('/api/admin/question-library/flows/', { params }),
    attachFlow: (
      id: number,
      data: { flow_id: number; order?: number; is_required?: boolean },
      params?: { include_inactive?: boolean },
    ) => api.post(`/api/admin/question-library/${id}/attach-flow/`, data, params ? { params } : undefined),
    detachFlow: (id: number, data: { flow_id: number }) =>
      api.post(`/api/admin/question-library/${id}/detach-flow/`, data),
    courseCatalog: () => api.get('/api/admin/question-library/course-catalog/'),
    courseTree: (courseId: number | 'unassigned', options?: { include_inactive?: boolean }) =>
      api.get('/api/admin/question-library/course-tree/', { params: { course_id: courseId, ...options } }),
    search: (params: QuestionLibrarySearchParams) =>
      api.get('/api/admin/question-library/search/', { params }),
    viewCounts: (params: Omit<QuestionLibrarySearchParams, 'view' | 'group' | 'duplicate_of' | 'page' | 'page_size'>) =>
      api.get('/api/admin/question-library/view-counts/', { params }),
  },
  radarDefinitions: {
    list: () => api.get('/api/admin/radar-definitions/'),
    retrieve: (key: string) =>
      api.get(`/api/admin/radar-definitions/${encodeURIComponent(key)}/`),
    create: (payload: Record<string, unknown>) =>
      api.post('/api/admin/radar-definitions/', payload),
    update: (key: string, payload: Record<string, unknown>) =>
      api.patch(`/api/admin/radar-definitions/${encodeURIComponent(key)}/`, payload),
    archive: (key: string) =>
      api.post(`/api/admin/radar-definitions/${encodeURIComponent(key)}/archive/`, {}),
    history: (key: string) =>
      api.get(`/api/admin/radar-definitions/${encodeURIComponent(key)}/history/`),
  },
  flows: {
    listSummary: (params?: {
      search?: string;
      page?: number;
      page_size?: number;
      course_id?: string | number;
      is_active?: boolean | string;
    }) => api.get<AdminCatalogPage<AdminFlowSummary>>('/api/admin/flow-builder/', {
      params: { ...params, summary: true },
    }),
    list: (params?: {
      search?: string;
      page?: number;
      page_size?: number;
      course_id?: string | number;
      is_active?: boolean | string;
    }) => api.get('/api/admin/flow-builder/', { params }),
    retrieve: (id: number) => api.get(`/api/admin/flow-builder/${id}/`),
    create: (payload: Record<string, unknown>) => api.post('/api/admin/flow-builder/', payload),
    update: (id: number, payload: Record<string, unknown>) =>
      api.patch(`/api/admin/flow-builder/${id}/`, payload),
    setQuestions: (
      id: number,
      questions: Array<{ question_id: number; is_required?: boolean }>,
    ) => api.post(`/api/admin/flow-builder/${id}/set-questions/`, { questions }),
    previewQuestions: (
      id: number,
      questions: Array<{ question_id: number; is_required?: boolean }>,
    ) => api.post(`/api/admin/flow-builder/${id}/preview/`, { questions }),
    quizQuestions: (id: number) =>
      api.get(`/api/admin/flow-builder/${id}/quiz-questions/`),
    quizReadiness: (id: number) =>
      api.get(`/api/admin/flow-builder/${id}/quiz-readiness/`),
    quizAttempts: (id: number, params?: { user_email?: string }) =>
      api.get(`/api/admin/flow-builder/${id}/quiz-attempts/`, { params }),
    resetQuizAttempts: (id: number, payload: { user_id?: number; user_email?: string }) =>
      api.post(`/api/admin/flow-builder/${id}/reset-quiz-attempts/`, payload),
    search: (params: ModuleLibrarySearchParams) =>
      api.get<AdminCatalogPage<ModuleLibraryRow>>('/api/admin/flow-builder/search/', { params }),
    viewCounts: (params: Omit<ModuleLibrarySearchParams, 'view' | 'page' | 'page_size'>) =>
      api.get<ModuleLibraryViewCounts>('/api/admin/flow-builder/view-counts/', { params }),
  },
  courses: {
    listSummary: (params?: { search?: string; page?: number; page_size?: number; is_active?: boolean | string }) =>
      api.get<AdminCatalogPage<AdminCourseSummary>>('/api/admin/course-composer/', {
        params: { ...params, summary: true },
      }),
    options: () => api.get<AdminCourseOption[]>('/api/admin/course-composer/options/'),
    list: (params?: { search?: string; page?: number }) =>
      api.get('/api/admin/course-composer/', { params }),
    retrieve: (id: number) => api.get(`/api/admin/course-composer/${id}/`),
    create: (payload: Record<string, unknown>) => api.post('/api/admin/course-composer/', payload),
    update: (id: number, payload: Record<string, unknown>) =>
      api.patch(`/api/admin/course-composer/${id}/`, payload),
    setCurriculum: (id: number, payload: CourseCurriculumPayload) =>
      api.post(`/api/admin/course-composer/${id}/curriculum/`, payload),
    setFlows: (
      id: number,
      flows: Array<{ flow_id: number; is_required?: boolean }>,
    ) => api.post(`/api/admin/course-composer/${id}/set-flows/`, { flows }),
    previewFlows: (
      id: number,
      flows: Array<{ flow_id: number; is_required?: boolean }>,
    ) => api.post(`/api/admin/course-composer/${id}/preview/`, { flows }),
  },
  mediaAssets: {
    list: (params?: {
      search?: string;
      media_type?: string;
      page?: number;
      page_size?: number;
      is_active?: boolean | string;
    }) => api.get('/api/admin/media-assets/', { params }),
  },
  mediaUpload: {
    list: () => api.get('/api/admin/media-uploads/'),
    startDrive: (payload: { drive_url: string }) =>
      api.post('/api/admin/media-uploads/', payload),
    startLocal: (files: File[]) => {
      const formData = new FormData();
      files.forEach((file) => formData.append('files', file));
      return api.post('/api/admin/media-uploads/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },
    retrieve: (id: string) => api.get(`/api/admin/media-uploads/${id}/`),
  },
  checklistItemTemplates: {
    list: () => api.get('/api/admin/checklist-item-templates/'),
    create: (payload: Record<string, unknown>) => api.post('/api/admin/checklist-item-templates/', payload),
    update: (id: string, payload: Record<string, unknown>) =>
      api.patch(`/api/admin/checklist-item-templates/${id}/`, payload),
    delete: (id: string) => api.delete(`/api/admin/checklist-item-templates/${id}/`),
  },
  checklistItemTemplateGroups: {
    list: () => api.get('/api/admin/checklist-item-template-groups/'),
    create: (payload: Record<string, unknown>) =>
      api.post('/api/admin/checklist-item-template-groups/', payload),
    update: (id: string, payload: Record<string, unknown>) =>
      api.patch(`/api/admin/checklist-item-template-groups/${id}/`, payload),
    delete: (id: string) => api.delete(`/api/admin/checklist-item-template-groups/${id}/`),
  },
  cohorts: {
    list: () => api.get('/api/admin/cohorts/'),
    retrieve: (id: string) => api.get(`/api/admin/cohorts/${id}/`),
    create: (payload: Record<string, unknown>) => api.post('/api/admin/cohorts/', payload),
    update: (id: string, payload: Record<string, unknown>) =>
      api.patch(`/api/admin/cohorts/${id}/`, payload),
    delete: (id: string) => api.delete(`/api/admin/cohorts/${id}/`),
    getContentDefaults: (id: string) => api.get(`/api/admin/cohorts/${id}/content-defaults/`),
    updateContentDefaults: (
      id: string,
      payload: {
        course_ids?: number[];
        flow_ids?: number[];
        override_org_course_defaults?: boolean;
        override_org_flow_defaults?: boolean;
        inspire_deck_ids?: string[];
        override_org_inspire_defaults?: boolean;
      },
    ) => api.patch(`/api/admin/cohorts/${id}/content-defaults/`, payload),
    userSearch: (params?: { search?: string; limit?: number; cohort_id?: string }) =>
      api.get('/api/admin/cohorts/user-search/', { params }),
    userSearchPaginated: (params?: { search?: string; page?: number; page_size?: number; cohort_id?: string }) =>
      api.get('/api/admin/cohorts/user-search/', { params }),
    listMembers: (id: string) => api.get(`/api/admin/cohorts/${id}/members/`),
    updateMembers: (
      id: string,
      payload: {
        members: Array<{
          user_id: string;
          role?: string;
          mentor_id?: string | null;
          is_current?: boolean;
        }>;
      },
    ) => api.post(`/api/admin/cohorts/${id}/members/`, payload),
    updateMentorPairings: (
      id: string,
      payload: {
        pairings: Array<{
          user_id: number;
          mentor_id: number | null;
        }>;
      },
    ) => api.patch<{
      updated: number;
      pairings: Array<{ user_id: number; mentor_id: number | null }>;
    }>(`/api/admin/cohorts/${id}/mentor-pairings/`, payload),
    removeMember: (id: string, userId: string) =>
      api.delete(`/api/admin/cohorts/${id}/members/${userId}/`),
    getAssignments: (id: string) => api.get(`/api/admin/cohorts/${id}/checklist-assignments/`),
    updateAssignments: (
      id: string,
      payload: {
        role: 'mentor' | 'mentee';
        template_ids?: string[];
        template_group_ids?: string[];
        apply_to_existing?: boolean;
      },
    ) => api.post(`/api/admin/cohorts/${id}/checklist-assignments/`, payload),
    bulkUpdateChecklistItems: (
      id: string,
      payload: {
        role: 'mentor' | 'mentee';
        template_id: string;
        title: string;
        description?: string;
        due_date: string;
        external_url?: string | null;
        media_asset_id?: string | null;
      },
    ) => api.patch(`/api/admin/cohorts/${id}/checklist-items/bulk-update/`, payload),
  },
  organizations: {
    list: (params?: { is_active?: boolean | string; search?: string }) =>
      api.get('/api/admin/organizations/', { params }),
    retrieve: (id: string) => api.get(`/api/admin/organizations/${id}/`),
    create: (payload: Record<string, unknown>) => api.post('/api/admin/organizations/', payload),
    update: (id: string, payload: Record<string, unknown>) =>
      api.patch(`/api/admin/organizations/${id}/`, payload),
    delete: (id: string) => api.delete(`/api/admin/organizations/${id}/`),
    getContentDefaults: (id: string) => api.get(`/api/admin/organizations/${id}/content-defaults/`),
    updateContentDefaults: (
      id: string,
      payload: {
        course_ids?: number[];
        flow_ids?: number[];
        inspire_deck_ids?: string[];
        inspire_program_name?: string;
        inspire_program_logo_id?: string | null;
        override_wisefriend_inspire_defaults?: boolean;
      },
    ) => api.patch(`/api/admin/organizations/${id}/content-defaults/`, payload),
  },
  signupCodes: {
    list: (params?: { organization_id?: string; cohort_id?: string }) =>
      api.get('/api/admin/signup-codes/', { params }),
    create: (payload: Record<string, unknown>) => api.post('/api/admin/signup-codes/', payload),
    update: (id: string, payload: Record<string, unknown>) =>
      api.patch(`/api/admin/signup-codes/${id}/`, payload),
    delete: (id: string) => api.delete(`/api/admin/signup-codes/${id}/`),
  },
  jobs: {
    runNightly: () => api.post<QueuedAdminJobResponse>('/api/admin/run-nightly-jobs/'),
    getAnalyticsStatus: () => api.get<AnalyticsOperationsStatus>('/api/admin/operations/analytics-rollups/'),
    runAnalyticsBackfill: () => api.post<QueuedAdminJobResponse>('/api/admin/run-analytics-backfill/'),
    runAnalyticsReconciliation: () =>
      api.post<QueuedAdminJobResponse>('/api/admin/run-analytics-reconciliation/'),
    runAnalyticsUserSummaryRefresh: () =>
      api.post<QueuedAdminJobResponse>('/api/admin/run-analytics-user-summary-refresh/'),
  },
  operations: {
    getVoiceProcessingModels: () =>
      api.get<VoiceProcessingModelsSetting>('/api/admin/operations/voice-processing-models/'),
    updateVoiceProcessingModels: (models: Partial<VoiceProcessingModels>) =>
      api.patch<VoiceProcessingModelsSetting>('/api/admin/operations/voice-processing-models/', models),
    getVoiceRuntime: () =>
      api.get<VoiceRuntimeSetting>('/api/admin/operations/voice-runtime/'),
    updateVoiceRuntime: (runtime: VoiceRuntime) =>
      api.patch<VoiceRuntimeSetting>('/api/admin/operations/voice-runtime/', { runtime }),
    getRoleplayPerformance: (window: RoleplayPerformanceWindow, signal?: AbortSignal) =>
      api.get<RoleplayPerformanceReport>('/api/admin/operations/roleplay-performance/', {
        params: { window }, signal, timeout: 15000,
      }),
    getVoiceRealtimeModel: () =>
      api.get<VoiceRealtimeModelSetting>('/api/admin/operations/voice-realtime-model/'),
    updateVoiceRealtimeModel: (realtimeModel: string | null) =>
      api.patch<VoiceRealtimeModelSetting>('/api/admin/operations/voice-realtime-model/', {
        realtime_model: realtimeModel,
      }),
    getVoiceTranscriptionModel: () =>
      api.get<VoiceTranscriptionModelSetting>('/api/admin/operations/voice-transcription-model/'),
    updateVoiceTranscriptionModel: (transcriptionModel: string | null) =>
      api.patch<VoiceTranscriptionModelSetting>('/api/admin/operations/voice-transcription-model/', {
        transcription_model: transcriptionModel,
      }),
    getRoleplayElevenLabsModel: () =>
      api.get<RoleplayElevenLabsModelSetting>('/api/admin/operations/roleplay-elevenlabs-model/'),
    updateRoleplayElevenLabsModel: (modelId: string | null) =>
      api.patch<RoleplayElevenLabsModelSetting>('/api/admin/operations/roleplay-elevenlabs-model/', {
        model_id: modelId,
      }),
    getElevenLabsRoleplayVoices: () =>
      api.get<ElevenLabsRoleplayVoiceLibrary>('/api/admin/operations/elevenlabs-roleplay-voices/'),
    searchElevenLabsSharedVoices: (search: string, page = 0) =>
      api.get<ElevenLabsSharedVoiceSearchResult>(
        '/api/admin/operations/elevenlabs-shared-voices/',
        { params: { search, page } },
      ),
    addElevenLabsSharedVoice: (
      publicOwnerId: string,
      voiceId: string,
      name: string,
    ) => api.post<ElevenLabsRoleplayVoiceLibrary>(
      '/api/admin/operations/elevenlabs-shared-voices/',
      { public_owner_id: publicOwnerId, voice_id: voiceId, name },
    ),
    getRoleplayVoiceOptions: () =>
      api.get<{ options: RoleplayVoiceOption[] }>('/api/admin/operations/roleplay-voice-options/'),
    suggestRoleplayScorecardCategories: (draft: RoleplayScorecardSuggestionRequest) =>
      api.post<RoleplayScorecardSuggestionResponse>(
        '/api/admin/roleplay/scorecard-suggestions/',
        draft,
      ),
    createElevenLabsRoleplayVoice: (voiceId: string) =>
      api.post<ElevenLabsRoleplayVoiceLibrary>(
        '/api/admin/operations/elevenlabs-roleplay-voices/',
        { voice_id: voiceId },
      ),
    updateElevenLabsRoleplayVoice: (
      alias: string,
      name: string,
      voiceId: string,
    ) => api.patch<ElevenLabsRoleplayVoiceLibrary>(
      `/api/admin/operations/elevenlabs-roleplay-voices/aliases/${encodeURIComponent(alias)}/`,
      { name, voice_id: voiceId },
    ),
    removeElevenLabsRoleplayVoice: (alias: string) =>
      api.delete<ElevenLabsRoleplayVoiceLibrary>(
        `/api/admin/operations/elevenlabs-roleplay-voices/aliases/${encodeURIComponent(alias)}/`,
      ),
    getMeetingScheduledEmails: () =>
      api.get<MeetingScheduledEmailsSetting>('/api/admin/operations/meeting-scheduled-emails/'),
    updateMeetingScheduledEmails: (enabled: boolean) =>
      api.patch<MeetingScheduledEmailsSetting>('/api/admin/operations/meeting-scheduled-emails/', {
        enabled,
      }),
  },
  users: {
    list: (params?: {
      search?: string;
      page?: number;
      page_size?: number;
      is_active?: string;
      is_staff?: string;
      organization_id?: string;
    }) => api.get('/api/admin/users/', { params }),
    retrieve: (id: number) => api.get(`/api/admin/users/${id}/`),
    create: (payload: Record<string, unknown>) => api.post('/api/admin/users/', payload),
    update: (id: number, payload: Record<string, unknown>) =>
      api.patch(`/api/admin/users/${id}/`, payload),
    listCohortMemberships: (id: number) =>
      api.get<AdminUserCohortMembership[]>(`/api/admin/users/${id}/cohort-memberships/`),
    addCohortMemberships: (
      id: number,
      payload: {
        cohort_ids: string[];
        role?: CohortRole;
        mentor_id?: number | null;
        current_cohort_id?: string | null;
      },
    ) => api.post<AdminUserCohortMembershipWriteResponse>(`/api/admin/users/${id}/cohort-memberships/`, payload),
    updateCohortMembership: (
      id: number,
      membershipId: string,
      payload: {
        role?: CohortRole;
        mentor_id?: number | null;
        is_current?: boolean;
      },
    ) => api.patch<AdminUserCohortMembership>(`/api/admin/users/${id}/cohort-memberships/${membershipId}/`, payload),
    removeCohortMembership: (id: number, membershipId: string) =>
      api.delete(`/api/admin/users/${id}/cohort-memberships/${membershipId}/`),
  },
  userTools: {
    getAssignments: (userId: string) =>
      api.get(`/api/admin/user-tools/assignments/${userId}/`),
    listFlowCompanionSessions: (userId: string) =>
      api.get<AdminFlowCompanionSummary[]>(`/api/admin/user-tools/flow-companion/${userId}/`),
    clearFlowCompanion: (payload: ClearFlowCompanionPayload) =>
      api.post<ClearFlowCompanionResponse>('/api/admin/user-tools/flow-companion/clear/', payload),
    assignCourse: (payload: { user_id: number; course_id: number; order: number }) =>
      api.post('/api/admin/user-tools/assign-course/', payload),
    assignFlow: (payload: { user_id: number; flow_id: number }) =>
      api.post('/api/admin/user-tools/assign-flow/', payload),
    removeCourseAssignment: (id: number) =>
      api.delete(`/api/admin/user-tools/course-assignment/${id}/`),
    removeFlowAssignment: (id: number) =>
      api.delete(`/api/admin/user-tools/flow-assignment/${id}/`),
    reorderCourses: (payload: { user_id: number; course_ids: number[] }) =>
      api.put('/api/admin/user-tools/reorder-courses/', payload),
    setCourses: (payload: { user_id: number; course_ids: number[] }) =>
      api.post('/api/admin/user-tools/set-courses/', payload),
    setFlows: (payload: { user_id: number; flow_ids: number[] }) =>
      api.post('/api/admin/user-tools/set-flows/', payload),
    listCourses: () => api.get('/api/courses/'),
    listFlows: (params?: { search?: string }) =>
      api.get('/api/flows/', { params }),
  },
};
